import { useEffect, useState } from 'react';
import { Modal } from '@shared/components/ui/Modal';
import { Shield } from 'lucide-react';
import type { OrgRole } from '@core/types';
import { diffRolePages } from '@core/auth/rolePageAccess';
import { ROLE_LABEL } from './settingsRoleLabels';

const BRAND = '#8B80F9';

const ROLE_ORDER: OrgRole[] = ['owner', 'admin', 'senior_recruiter', 'junior_recruiter', 'viewer'];

type Props = {
  open: boolean;
  onClose: () => void;
  memberName: string;
  fromRole: OrgRole;
  onConfirm: (toRole: OrgRole) => void;
  saving?: boolean;
};

export function RoleChangeImpactModal({
  open,
  onClose,
  memberName,
  fromRole,
  onConfirm,
  saving,
}: Props) {
  const [toRole, setToRole] = useState<OrgRole>(fromRole);

  useEffect(() => {
    if (open) {
      const next = ROLE_ORDER.find((r) => r !== fromRole) ?? fromRole;
      setToRole(next);
    }
  }, [open, fromRole]);

  const { gain, lose } = diffRolePages(fromRole, toRole);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Confirm role change"
      subtitle={memberName}
      icon={<Shield className="h-4 w-4 text-white" />}
      size="md"
    >
      <div className="space-y-4 -mx-1">
        <p className="text-sm vscode-text-secondary leading-relaxed">
          This updates what they can open in the sidebar and whether they can manage team, billing, or exports. It does not
          remove their Keycloak login — only what this workspace allows.
        </p>

        <div>
          <label className="mb-1 block text-xs vscode-text-tertiary">New role</label>
          <select
            className="input w-full"
            value={toRole}
            onChange={(e) => setToRole(e.target.value as OrgRole)}
          >
            {ROLE_ORDER.filter((r) => r !== 'owner' || fromRole === 'owner').map((r) => (
              <option key={r} value={r} disabled={r === 'owner' && fromRole !== 'owner'}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[10px] vscode-text-tertiary">
            From <span className="vscode-text-secondary">{ROLE_LABEL[fromRole]}</span>
            {' → '}
            <span className="vscode-text-secondary">{ROLE_LABEL[toRole]}</span>
          </p>
        </div>

        {toRole === fromRole ? (
          <p className="text-xs text-amber-400/90">Choose a different role to see access changes.</p>
        ) : null}

        {gain.length > 0 && (
          <div className="rounded-lg border border-emerald-800/40 bg-emerald-950/25 px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-400/90 mb-1.5">Will gain access to</p>
            <ul className="text-xs text-emerald-100/95 space-y-1 list-disc pl-4">
              {gain.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        )}

        {lose.length > 0 && (
          <div className="rounded-lg border border-amber-800/40 bg-amber-950/20 px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-400/90 mb-1.5">Will lose access to</p>
            <ul className="text-xs text-amber-100/90 space-y-1 list-disc pl-4">
              {lose.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        )}

        {toRole !== fromRole && gain.length === 0 && lose.length === 0 && (
          <p className="text-xs vscode-text-tertiary">No navigation change at this coarse level — roles may still differ in fine-grained actions.</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary text-sm" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            style={{ backgroundColor: BRAND }}
            onClick={() => onConfirm(toRole)}
            disabled={saving || toRole === fromRole}
          >
            {saving ? 'Saving…' : 'Apply role'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
